import { type ReactNode, useId } from 'react';
import { cn } from '#client/lib/cn';
import { ChartTable } from './ChartTable';
import { Legend } from './Legend';
import { describeChart } from './categories';
import {
  type ChartMargin,
  DEFAULT_MARGIN,
  type Formatter,
  type PlotBox,
  type Series,
  plotBox,
} from './types';
import { useChartWidth } from './useChartWidth';

interface ChartFrameProps {
  /** The chart's accessible name. Required — an unnamed chart is decoration. */
  title: string;
  /** One sentence of takeaway, e.g. "Median 6.2 hours, rising." */
  summary?: string | undefined;
  series: readonly Series[];
  /** Draws the chart, given the box it has to fill. */
  children: (plot: PlotBox) => ReactNode;
  /**
   * HTML drawn over the plot — the focusable cursor, the tooltip, the live
   * region.
   *
   * A second render prop rather than a `foreignObject` inside the SVG. Foreign
   * objects clip unpredictably in Safari and swallow pointer events at the
   * edges, and the tooltip has to be able to overflow the plot box to stay on
   * screen near the axis.
   */
  overlay?: ((plot: PlotBox) => ReactNode) | undefined;
  height?: number | undefined;
  margin?: Partial<ChartMargin> | undefined;
  format?: Formatter | undefined;
  legend?: boolean | undefined;
  /** Column header for the hidden table's category column. */
  categoryLabel?: string | undefined;
  className?: string | undefined;
}

const DEFAULT_FORMAT: Formatter = (value) => String(value);

/**
 * Everything every chart needs and no chart should reimplement.
 *
 * Measures its own width, reserves the plot box, renders the drawing its child
 * returns, and — the part that matters — carries the four accessibility layers
 * so that no individual chart type can forget one:
 *
 *   1. `role="img"` with a generated name. Enough on its own for a short
 *      series, which is the case `Sparkline` already handles this way.
 *   2. A visually-hidden `<table>`, referenced by `aria-describedby`, for
 *      anything longer than a name can carry.
 *   3. A single focusable cursor with arrow-key navigation, and a live region
 *      that speaks only for the keyboard — both supplied by the chart through
 *      the `overlay` render prop, which every categorical chart fills with
 *      `CursorLayer`. Never one tab stop per data point.
 *   4. A legend whose swatches carry each series' marker and dash, not just
 *      its colour.
 *
 * Width starts at zero and the drawing is withheld until it is known. Rendering
 * at a guessed width and snapping to the real one is a visible jump on every
 * page load.
 */
export function ChartFrame({
  title,
  summary,
  series,
  children,
  overlay,
  height = 240,
  margin,
  format = DEFAULT_FORMAT,
  legend = true,
  categoryLabel = 'Category',
  className,
}: ChartFrameProps) {
  const { ref, width } = useChartWidth();
  const tableId = useId();

  const box = plotBox(width, height, { ...DEFAULT_MARGIN, ...margin });
  const { ariaLabel, usesTable } = describeChart({ title, summary, series }, format);

  return (
    <figure className={cn('c-chart', className)}>
      {legend && <Legend series={series} className="c-chart__legend" />}

      <div className="c-chart__plot" ref={ref} style={{ blockSize: `${height}px` }}>
        {width > 0 && (
          <svg
            className="c-chart__svg"
            viewBox={`0 0 ${width} ${height}`}
            width={width}
            height={height}
            role="img"
            aria-label={ariaLabel}
            aria-describedby={usesTable ? tableId : undefined}
          >
            <g transform={`translate(${box.margin.left} ${box.margin.top})`}>{children(box)}</g>
          </svg>
        )}
        {width > 0 && overlay && <ChartOverlay box={box}>{overlay(box)}</ChartOverlay>}
      </div>

      {summary && <figcaption className="c-chart__summary">{summary}</figcaption>}

      {usesTable && (
        <ChartTable
          id={tableId}
          title={title}
          categoryLabel={categoryLabel}
          series={series}
          format={format}
        />
      )}
    </figure>
  );
}

/** The HTML layer laid exactly over the plot box. */
function ChartOverlay({ box, children }: { box: PlotBox; children: ReactNode }) {
  return (
    <div
      className="c-chart__overlay"
      style={{
        insetInlineStart: `${box.margin.left}px`,
        insetBlockStart: `${box.margin.top}px`,
        inlineSize: `${box.inner.width}px`,
        blockSize: `${box.inner.height}px`,
      }}
    >
      {children}
    </div>
  );
}
